import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "boardViewBtn", "stepBackBtn", "filterBar", "normalView", "zoomView",
    "characterDropdown", "locationDropdown", "plotDropdown",
    "characterList", "locationList", "plotList"
  ]

  static values = {
    zoomed: { type: Boolean, default: false },
    selectedCharacters: { type: Array, default: [] },
    selectedLocations: { type: Array, default: [] },
    selectedPlots: { type: Array, default: [] }
  }

  connect() {
    this.closeDropdownHandler = this.closeAllDropdowns.bind(this)
    document.addEventListener('click', this.closeDropdownHandler)
  }

  disconnect() {
    document.removeEventListener('click', this.closeDropdownHandler)
  }

  toggleZoom() {
    this.zoomedValue = !this.zoomedValue
    this.updateView()
  }

  showBoardView() {
    this.zoomedValue = false
    this.updateView()
  }

  toggleStepBack() {
    this.zoomedValue = !this.zoomedValue
    this.updateView()
  }

  updateView() {
    const activeClasses = ['bg-white', 'text-gray-700', 'shadow-sm']
    const inactiveClasses = ['text-gray-600', 'hover:bg-white', 'hover:shadow-sm']

    if (this.zoomedValue) {
      // Show zoomed out (step back) view
      this.normalViewTargets.forEach(el => el.classList.add('hidden'))
      if (this.hasZoomViewTarget) this.zoomViewTarget.classList.remove('hidden')
      if (this.hasFilterBarTarget) this.filterBarTarget.classList.remove('hidden')

      // Update button states
      if (this.hasBoardViewBtnTarget) {
        this.boardViewBtnTarget.classList.remove(...activeClasses)
        this.boardViewBtnTarget.classList.add(...inactiveClasses)
      }
      if (this.hasStepBackBtnTarget) {
        this.stepBackBtnTarget.classList.remove(...inactiveClasses)
        this.stepBackBtnTarget.classList.add(...activeClasses)
      }
    } else {
      // Show normal board view
      this.normalViewTargets.forEach(el => el.classList.remove('hidden'))
      if (this.hasZoomViewTarget) this.zoomViewTarget.classList.add('hidden')
      if (this.hasFilterBarTarget) this.filterBarTarget.classList.add('hidden')

      // Update button states
      if (this.hasBoardViewBtnTarget) {
        this.boardViewBtnTarget.classList.remove(...inactiveClasses)
        this.boardViewBtnTarget.classList.add(...activeClasses)
      }
      if (this.hasStepBackBtnTarget) {
        this.stepBackBtnTarget.classList.remove(...activeClasses)
        this.stepBackBtnTarget.classList.add(...inactiveClasses)
      }
    }

    // Reinitialize Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }
  }

  // Dropdown toggles
  toggleCharacterDropdown(event) {
    event.stopPropagation()
    const wasHidden = this.hasCharacterDropdownTarget && this.characterDropdownTarget.classList.contains('hidden')
    this.closeAllDropdowns()
    if (wasHidden && this.hasCharacterDropdownTarget) {
      this.characterDropdownTarget.classList.remove('hidden')
    }
  }

  toggleLocationDropdown(event) {
    event.stopPropagation()
    const wasHidden = this.hasLocationDropdownTarget && this.locationDropdownTarget.classList.contains('hidden')
    this.closeAllDropdowns()
    if (wasHidden && this.hasLocationDropdownTarget) {
      this.locationDropdownTarget.classList.remove('hidden')
    }
  }

  togglePlotDropdown(event) {
    event.stopPropagation()
    const wasHidden = this.hasPlotDropdownTarget && this.plotDropdownTarget.classList.contains('hidden')
    this.closeAllDropdowns()
    if (wasHidden && this.hasPlotDropdownTarget) {
      this.plotDropdownTarget.classList.remove('hidden')
    }
  }

  closeAllDropdowns(event) {
    // Don't close if clicking inside a dropdown
    if (event && event.target) {
      const clickedDropdown = event.target.closest('[data-board-zoom-target="characterDropdown"], [data-board-zoom-target="locationDropdown"], [data-board-zoom-target="plotDropdown"]')
      if (clickedDropdown) return
    }
    if (this.hasCharacterDropdownTarget) this.characterDropdownTarget.classList.add('hidden')
    if (this.hasLocationDropdownTarget) this.locationDropdownTarget.classList.add('hidden')
    if (this.hasPlotDropdownTarget) this.plotDropdownTarget.classList.add('hidden')
  }

  // Filter selection
  toggleCharacterFilter(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.characterId)
    this.toggleFilter('selectedCharacters', id, event.currentTarget)
  }

  toggleLocationFilter(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.locationId)
    this.toggleFilter('selectedLocations', id, event.currentTarget)
  }

  togglePlotFilter(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.plotId)
    this.toggleFilter('selectedPlots', id, event.currentTarget)
  }

  toggleFilter(arrayName, id, button) {
    const currentArray = this[`${arrayName}Value`] || []
    const index = currentArray.indexOf(id)

    if (index > -1) {
      currentArray.splice(index, 1)
      button.classList.remove('bg-emerald-500', 'text-white')
      button.classList.add('hover:bg-gray-100')
    } else {
      currentArray.push(id)
      button.classList.add('bg-emerald-500', 'text-white')
      button.classList.remove('hover:bg-gray-100')
    }

    this[`${arrayName}Value`] = [...currentArray]
    this.applyFilters()
  }

  clearFilters() {
    this.selectedCharactersValue = []
    this.selectedLocationsValue = []
    this.selectedPlotsValue = []

    // Reset all filter button styles
    this.element.querySelectorAll('[data-character-id], [data-location-id], [data-plot-id]').forEach(btn => {
      btn.classList.remove('bg-emerald-500', 'text-white')
      btn.classList.add('hover:bg-gray-100')
    })

    this.applyFilters()
  }

  applyFilters() {
    const cards = this.element.querySelectorAll('[data-zoom-card]')
    const hasFilters = this.selectedCharactersValue.length > 0 ||
                       this.selectedLocationsValue.length > 0 ||
                       this.selectedPlotsValue.length > 0

    cards.forEach(card => {
      if (!hasFilters) {
        card.classList.remove('opacity-30', 'grayscale')
        return
      }

      const cardCharacters = JSON.parse(card.dataset.characterIds || '[]')
      const cardLocations = JSON.parse(card.dataset.locationIds || '[]')
      const cardPlots = JSON.parse(card.dataset.plotIds || '[]')

      let matches = true

      // Check if card matches ALL selected filters (AND logic within and between categories)
      if (this.selectedCharactersValue.length > 0) {
        const hasAllCharacters = this.selectedCharactersValue.every(id => cardCharacters.includes(id))
        if (!hasAllCharacters) matches = false
      }

      if (this.selectedLocationsValue.length > 0) {
        const hasAllLocations = this.selectedLocationsValue.every(id => cardLocations.includes(id))
        if (!hasAllLocations) matches = false
      }

      if (this.selectedPlotsValue.length > 0) {
        const hasAllPlots = this.selectedPlotsValue.every(id => cardPlots.includes(id))
        if (!hasAllPlots) matches = false
      }

      if (matches) {
        card.classList.remove('opacity-30', 'grayscale')
      } else {
        card.classList.add('opacity-30', 'grayscale')
      }
    })
  }
}
